import { useState } from 'react';
import { generate, type Difficulty } from '../sudoku';
import { PlayScreen } from '../components/PlayScreen';
import { deserialize, initGame } from '../game/state';
import { listLocal, removeLocal } from '../game/storage';
import { deleteRemote } from '../game/sync';
import type { GameState } from '../game/types';

const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard', 'expert'];

function newGame(difficulty: Difficulty): GameState {
  const puzzle = generate(difficulty);
  const id = crypto.randomUUID();
  return initGame({ key: `level:${id}`, kind: 'level', difficulty: puzzle.difficulty, givens: puzzle.givens });
}

/** Most recent unfinished practice game, if any, for a one-tap resume. */
function findResumable(): GameState | null {
  const stored = listLocal().find((g) => g.kind === 'level' && g.status === 'playing');
  return stored ? deserialize(stored) : null;
}

export function Levels() {
  const [game, setGame] = useState<GameState | null>(findResumable);

  if (game) {
    return (
      <PlayScreen
        key={game.key}
        initial={game}
        title={
          <span>
            Practice · <span className="diff-tag">{game.difficulty}</span>
          </span>
        }
        onNewGame={() => setGame(newGame(game.difficulty))}
        solvedContent={
          <button type="button" className="link" onClick={() => discard(game, setGame)}>
            Back to levels
          </button>
        }
      />
    );
  }

  return (
    <section className="levels">
      <h1>Practice</h1>
      <p>Unlimited puzzles, generated on your device. Playable offline.</p>
      <div className="level-grid">
        {DIFFICULTIES.map((d) => (
          <button key={d} type="button" className={`level-card ${d}`} onClick={() => setGame(newGame(d))}>
            <span className="level-name">{d}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function discard(game: GameState, setGame: (g: GameState | null) => void) {
  removeLocal(game.key);
  void deleteRemote(game.key);
  setGame(null);
}
